import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import WhatsAppIcon from 'components/icons/WhatsAppIcon';

const TestimonialsSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [showSwipeHints, setShowSwipeHints] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  const testimonials = [
    {
      id: 1,
      name: "Sarah C.",
      university: "Stanford University",
      course: "Computer Science",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
      rating: 5,
      beforeGrade: "C+",
      afterGrade: "A-",
      testimonial: `I was struggling with my data structures assignment and had only 2 days left. Somo Academia matched me with an expert who not only completed my assignment perfectly but also explained the concepts so well that I aced my next exam. The $40 I spent saved my GPA and taught me more than weeks of struggling alone.`,
      subject: "Data Structures",
      verified: true,
      linkedIn: "https://linkedin.com/in/sarahchen"
    },
    {
      id: 2,
      name: "Marcus J.",
      university: "University of Michigan",
      course: "Business Administration",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      rating: 5,
      beforeGrade: "D",
      afterGrade: "B+",
      testimonial: `As a working student, I barely had time for my marketing research paper. The team at Somo Academia delivered a comprehensive 15-page analysis that not only met all requirements but exceeded my professor's expectations. The transparent pricing meant no surprises - exactly $120 as quoted.`,
      subject: "Marketing Research",
      verified: true,
      linkedIn: "https://linkedin.com/in/marcusjohnson"
    },
    {
      id: 3,
      name: "Emily R.",
      university: "UCLA",
      course: "Psychology",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      rating: 5,
      beforeGrade: "C",
      afterGrade: "A",
      testimonial: `I was skeptical about online academic help, but Somo Academia changed my mind completely. Their psychology expert helped me understand complex statistical concepts while completing my research methodology assignment. The plagiarism report gave me confidence, and the grade improvement speaks for itself.`,
      subject: "Research Methodology",
      verified: true,
      linkedIn: "https://linkedin.com/in/emilyrodriguez"
    },
    {
      id: 4,
      name: "David K.",
      university: "MIT",
      course: "Mechanical Engineering",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      rating: 5,
      beforeGrade: "C-",
      afterGrade: "A-",
      testimonial: `The thermodynamics problem set was impossible for me to solve alone. Within hours of contacting Somo Academia, I was connected with a PhD-level expert who walked me through each solution step by step. Not only did I submit on time, but I finally understood the concepts that had been confusing me all semester.`,
      subject: "Thermodynamics",
      verified: true,
      linkedIn: "https://linkedin.com/in/davidkim"
    },
    {
      id: 5,
      name: "Aisha P.",
      university: "Harvard University",
      course: "Economics",
      avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&h=150&fit=crop&crop=face",
      rating: 5,
      beforeGrade: "B-",
      afterGrade: "A",
      testimonial: `My econometrics final project was worth 40% of my grade, and I was completely lost with the statistical analysis. Somo Academia's expert not only completed the analysis perfectly but also provided detailed explanations that helped me present confidently. The investment in my education was worth every penny.`,
      subject: "Econometrics",
      verified: true,
      linkedIn: "https://linkedin.com/in/aishapatel"
    }
  ];

  const universities = [
    { name: "Stanford", logo: "https://images.unsplash.com/photo-1562774053-701939374585?w=100&h=100&fit=crop" },
    { name: "MIT", logo: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=100&h=100&fit=crop" },
    { name: "Harvard", logo: "https://images.unsplash.com/photo-1607237138185-eedd9c632b0b?w=100&h=100&fit=crop" },
    { name: "UCLA", logo: "https://images.unsplash.com/photo-1564981797816-1043664bf78d?w=100&h=100&fit=crop" },
    { name: "Michigan", logo: "https://images.unsplash.com/photo-1576495199011-eb94736d05d6?w=100&h=100&fit=crop" }
  ];

  useEffect(() => {
    if (isAutoPlaying) {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % testimonials.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [isAutoPlaying, testimonials.length]);

  // Mobile detection and swipe hints management
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      
      if (mobile && showSwipeHints) {
        // Auto-hide swipe hints after 4 seconds on mobile
        const hintTimer = setTimeout(() => {
          setShowSwipeHints(false);
        }, 4000);
        return () => clearTimeout(hintTimer);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, [showSwipeHints]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % testimonials.length);
    setIsAutoPlaying(false);
    setShowSwipeHints(false);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    setIsAutoPlaying(false);
    setShowSwipeHints(false);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setShowSwipeHints(false);
  };

  // Pause/resume handlers
  const handleMouseEnter = () => {
    setIsAutoPlaying(false);
  };

  const handleMouseLeave = () => {
    setIsAutoPlaying(true);
  };

  const handleTouchStart = () => {
    setIsAutoPlaying(false);
  };

  const handleTouchEnd = () => {
    // Resume after a short delay
    setTimeout(() => {
      setIsAutoPlaying(true);
    }, 3000);
  };

  return (
    <section id="reviews" className="py-20 bg-surface/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-text-primary mb-6">
            Real Students, <span className="text-gradient">Real Results</span>
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Don't just take our word for it. See how we've helped thousands of students 
            improve their grades and reduce academic stress.
          </p>
        </motion.div>

        {/* Mobile Swipe Hints - Only show on mobile */}
        {showSwipeHints && isMobile && (
          <div className="lg:hidden absolute left-4 right-4 top-1/2 flex justify-between z-10 pointer-events-none max-w-4xl mx-auto" style={{ transform: 'translateY(200px)' }}>
            <motion.div
              animate={{ 
                x: [-8, 8, -8],
                opacity: [0.6, 1, 0.6]
              }}
              transition={{ 
                repeat: Infinity, 
                duration: 2,
                ease: "easeInOut"
              }}
              className="bg-white/90 rounded-full p-2 shadow-lg"
            >
              <Icon name="ChevronLeft" size={20} className="text-primary" />
            </motion.div>
            <motion.div
              animate={{ 
                x: [8, -8, 8],
                opacity: [0.6, 1, 0.6]
              }}
              transition={{ 
                repeat: Infinity, 
                duration: 2,
                ease: "easeInOut"
              }}
              className="bg-white/90 rounded-full p-2 shadow-lg"
            >
              <Icon name="ChevronRight" size={20} className="text-primary" />
            </motion.div>
          </div>
        )}

        {/* Testimonials Carousel */}
        <div 
          className="relative max-w-4xl mx-auto mb-16"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div className="overflow-hidden rounded-2xl">
            <motion.div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="w-full flex-shrink-0">
                  <div className="glass-card p-8 mx-4 hover-lift relative overflow-hidden group">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center space-x-4">
                        <div className="relative">
                          <Image
                            src={testimonial.avatar}
                            alt={testimonial.name}
                            className="w-16 h-16 rounded-full object-cover"
                          />
                          {testimonial.verified && (
                            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-success rounded-full flex items-center justify-center">
                              <Icon name="Check" size={12} color="white" />
                            </div>
                          )}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-text-primary">
                            {testimonial.name}
                          </h3>
                          <p className="text-text-secondary text-sm">
                            {testimonial.course} • {testimonial.university}
                          </p>
                          <div className="flex items-center space-x-1 mt-1">
                            {[...Array(testimonial.rating)].map((_, i) => (
                              <Icon key={i} name="Star" size={16} className="text-warning fill-current" />
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Grade Improvement */}
                      <div className="text-right">
                        <div className="text-sm text-text-muted mb-1">Grade Improvement</div>
                        <div className="flex items-center space-x-2">
                          <span className="text-error font-semibold">{testimonial.beforeGrade}</span>
                          <Icon name="ArrowRight" size={16} className="text-text-muted" />
                          <span className="text-success font-semibold">{testimonial.afterGrade}</span>
                        </div>
                        <div className="text-xs text-text-muted mt-1">{testimonial.subject}</div>
                      </div>
                    </div>

                    {/* Testimonial Text */}
                    <blockquote className="text-text-secondary text-lg leading-relaxed mb-6">
                      "{testimonial.testimonial}"
                    </blockquote>

                    {/* Footer */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2 text-sm text-text-muted">
                        <Icon name="Shield" size={16} className="text-success" />
                        <span>Verified Student</span>
                      </div>
                      <a
                        href={testimonial.linkedIn}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-2 text-sm text-primary hover:text-primary-600 transition-colors"
                      >
                        <Icon name="ExternalLink" size={16} />
                        <span>View LinkedIn</span>
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Navigation Buttons */}
          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4 w-12 h-12 bg-surface hover:bg-surface-800 rounded-full flex items-center justify-center transition-colors border border-border"
            aria-label="Previous testimonial"
          >
            <Icon name="ChevronLeft" size={24} className="text-text-primary" />
          </button>

          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4 w-12 h-12 bg-surface hover:bg-surface-800 rounded-full flex items-center justify-center transition-colors border border-border"
            aria-label="Next testimonial"
          >
            <Icon name="ChevronRight" size={24} className="text-text-primary" />
          </button>

          {/* Dots Indicator */}
          <div className="flex justify-center space-x-2 mt-6">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentSlide ? 'bg-primary' : 'bg-surface border border-border'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* University Logos
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <h3 className="text-xl font-semibold text-text-primary mb-8">
            Trusted by Students from Top Universities
          </h3>
          <div className="flex flex-wrap justify-center items-center space-x-8 opacity-60">
            {universities.map((university, index) => (
              <motion.div
                key={university.name}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex flex-col items-center space-y-2 mb-6"
              >
                <Image
                  src={university.logo}
                  alt={`${university.name} logo`}
                  className="w-16 h-16 rounded-full object-cover grayscale hover:grayscale-0 transition-all duration-250"
                />
                <span className="text-sm text-text-muted">{university.name}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <h3 className="text-2xl font-bold text-text-primary mb-4">
            Ready to Join These Success Stories?
          </h3>
          <p className="text-text-secondary mb-6 max-w-2xl mx-auto">
            Don't let academic stress hold you back. Get the same quality help that 
            transformed these students' grades and confidence.
          </p>
          <motion.button
            onClick={() => {
              const phoneNumber = '254793863320';
              const message = encodeURIComponent('Hi! I saw the amazing student testimonials and want to improve my grades too. Can you help me with my assignment?');
              window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
            }}
            whileHover={{ 
              scale: 1.02,
              boxShadow: "0 10px 25px rgba(37, 211, 102, 0.3)"
            }}
            whileTap={{ 
              scale: 0.98,
              transition: { duration: 0.1 }
            }}
            className="relative text-lg px-8 py-4 bg-gradient-to-r from-[#25D366] to-[#128C7E] hover:from-[#128C7E] hover:to-[#25D366] text-white rounded-xl shadow-lg font-bold inline-flex items-center space-x-3 transition-all duration-300 overflow-hidden group"
          >
            <div className="absolute inset-0 bg-white/10 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-out"></div>
            <WhatsAppIcon size={24} className="relative z-10 text-white" />
            <span className="relative z-10">Start Your Success Story</span>
            <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer"></div>
            </div>
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
